import type { DeckMaterialItem, DeckMaterialRef } from './types';

/**
 * String form of a material ref — the pane's URL / persisted-tab grammar,
 * shared by web (`?tab=`) and mobile (last-visited tab):
 *
 *   `all`                 — the overview grid
 *   `source:{sourceId}`   — one source
 *   `note:{noteId}`       — one note
 *   `mindmap:{id}`        — one mind map
 *
 * Three LEGACY aliases keep every pre-multi-source link and persisted value
 * working: `document` (the PRIMARY source — wherever a reorder has drawn it),
 * `notes` (the first note as drawn) and `mindmaps` (the first map as drawn).
 * They are read, never written.
 */

export const MATERIALS_OVERVIEW_TAB = 'all';

export type LegacyDocTab = 'document' | 'notes' | 'mindmaps';

export function encodeMaterialRef(ref: DeckMaterialRef): string {
  switch (ref.type) {
    case 'source':
      return `source:${ref.sourceId}`;
    case 'note':
      return `note:${ref.noteId}`;
    case 'mindmap':
      return `mindmap:${ref.mindmapId}`;
  }
}

/** Parse the canonical form only (no legacy aliases). `null` when malformed. */
export function decodeMaterialRef(
  raw: string | null | undefined,
): DeckMaterialRef | null {
  if (!raw) return null;
  const cut = raw.indexOf(':');
  if (cut <= 0) return null;
  const type = raw.slice(0, cut);
  const id = raw.slice(cut + 1);
  if (!id) return null;
  switch (type) {
    case 'source':
      return { type: 'source', sourceId: id };
    case 'note':
      return { type: 'note', noteId: id };
    case 'mindmap':
      return { type: 'mindmap', mindmapId: id };
    default:
      return null;
  }
}

export function sameMaterialRef(a: DeckMaterialRef, b: DeckMaterialRef): boolean {
  return encodeMaterialRef(a) === encodeMaterialRef(b);
}

export function findMaterial(
  items: readonly DeckMaterialItem[],
  ref: DeckMaterialRef,
): DeckMaterialItem | undefined {
  const key = encodeMaterialRef(ref);
  return items.find((item) => item.key === key);
}

/** First item of a kind group AS DRAWN — the target of the `notes` /
 *  `mindmaps` aliases. Never the target of `document`: see
 *  {@link landingSourceItem}. */
export function firstMaterialOfType(
  items: readonly DeckMaterialItem[],
  type: DeckMaterialRef['type'],
): DeckMaterialItem | undefined {
  return items.find((item) => item.ref.type === type);
}

/**
 * The document a deck OPENS ON: its primary source, wherever the user has
 * dragged it in the strip, else (primary unknown or not readable) the first
 * source drawn. `items` are in DRAWN order, so "the first source item" is not
 * the primary once anything has been reordered — a reorder moves where a
 * material is drawn, never which one is the primary.
 */
export function landingSourceItem(
  items: readonly DeckMaterialItem[],
  primarySourceId: string | undefined,
): DeckMaterialItem | undefined {
  const primary = primarySourceId
    ? findMaterial(items, { type: 'source', sourceId: primarySourceId })
    : undefined;
  return primary ?? firstMaterialOfType(items, 'source');
}

export type DecodedMaterialsTab =
  | { view: 'overview' }
  | { view: 'item'; ref: DeckMaterialRef };

/**
 * Resolve a raw tab value against the deck's current items. Canonical refs
 * must point at an existing item; `document` resolves to the landing source
 * (the primary, see {@link landingSourceItem}); `notes` / `mindmaps` to the
 * first item of their group. `null` = unknown or dangling → the caller falls
 * back to its default (the pane never shows an empty item).
 */
export function decodeMaterialsTab(
  raw: string | null | undefined,
  items: readonly DeckMaterialItem[],
  // Required, not optional: a caller that forgets it would silently resolve
  // `document` to whatever is drawn first.
  primarySourceId: string | undefined,
): DecodedMaterialsTab | null {
  if (!raw) return null;
  if (raw === MATERIALS_OVERVIEW_TAB) return { view: 'overview' };

  const legacy: Record<LegacyDocTab, DeckMaterialRef['type']> = {
    document: 'source',
    notes: 'note',
    mindmaps: 'mindmap',
  };
  if (raw in legacy) {
    const first =
      raw === 'document'
        ? landingSourceItem(items, primarySourceId)
        : firstMaterialOfType(items, legacy[raw as LegacyDocTab]);
    return first ? { view: 'item', ref: first.ref } : null;
  }

  const ref = decodeMaterialRef(raw);
  if (!ref) return null;
  return findMaterial(items, ref) ? { view: 'item', ref } : null;
}

/**
 * The legacy three-way tab a ref belongs to — for consumers that still key
 * on `document | notes | mindmaps` (coach marks, the source-jump bridge).
 * Every source, primary or not, is a `document` view.
 */
export function legacyDocTabOfRef(ref: DeckMaterialRef): LegacyDocTab {
  switch (ref.type) {
    case 'source':
      return 'document';
    case 'note':
      return 'notes';
    case 'mindmap':
      return 'mindmaps';
  }
}
